import { useSearchParams, Link } from "react-router-dom";
import { Clock, Mail, CheckCircle2, ArrowRight } from "lucide-react";

import Header from "./Header";
import Footer from "./Footer";

/**
 * Landing page after a customer submits an order inside the 5-business-day
 * rush window. Backend has already created the order in `pending_approval`
 * status and emailed admin — this page explains what happens next.
 */
export default function RushPendingPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || "";
  const days = params.get("days") || "";
  const daysLabel = days
    ? `${days} business day${String(days) === "1" ? "" : "s"}`
    : "fewer than our standard 5 business days";

  return (
    <div className="min-h-screen bg-brand-cream/40 flex flex-col">
      <Header />
      <main className="flex-1 pt-[88px] sm:pt-[100px] pb-16">
        <div className="max-w-xl mx-auto px-5 sm:px-6">
          <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-7 sm:p-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700">
                  Pending our approval
                </p>
                <h1
                  data-testid="rush-pending-title"
                  className="font-display font-black text-3xl sm:text-4xl text-brand-charcoal leading-tight"
                >
                  Rush order received.
                </h1>
              </div>
            </div>

            <p className="text-[15px] text-brand-charcoal/80 leading-relaxed">
              Thanks for choosing <strong>Fresh Fast Foods</strong>. Your event is{" "}
              <strong>{daysLabel}</strong> away, which is inside our standard 5-business-day
              lead time, so we need to review your order before confirming it.
              <strong> No payment has been taken yet.</strong>
            </p>

            <div className="mt-6 rounded-2xl bg-brand-cream/60 p-4 sm:p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60 mb-3">
                What happens next
              </p>
              <ul className="space-y-3 text-[14px] text-brand-charcoal/85">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
                  <span>
                    Our team has been notified immediately via email and SMS.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Mail className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
                  <span>
                    You'll get an email within a few hours (during business hours)
                    confirming whether we can take it on.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <ArrowRight className="h-4 w-4 text-brand-teal mt-0.5 shrink-0" />
                  <span>
                    If approved, you'll receive a secure payment link in that same
                    email. Nothing is locked in until you pay.
                  </span>
                </li>
              </ul>
            </div>

            {orderId && (
              <p
                data-testid="rush-pending-order-id"
                className="mt-5 text-[11.5px] text-brand-charcoal/55 font-mono"
              >
                Reference: {orderId.slice(0, 8).toUpperCase()}
              </p>
            )}

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/"
                data-testid="rush-pending-home-link"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
              >
                Back to Home
              </Link>
              <a
                href="tel:+19292725704"
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                Call us: (929) 272-5704
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
